import { useState } from "react";
import { materialProposalBasis, materialsApproved, validMaterialAllowance, scopeNeedsNoPurchaseMaterials,
  type MaterialAllowanceApproval } from "@workspace/api-zod/material-allowance";
import { lineMaterialRows, type MaterialsLine } from "@workspace/api-zod/materials-list";
import { useAutomaticMaterials } from "./use-automatic-materials";
import { MaterialAllowanceControl } from "./material-allowance-control";

/** The same explicit lookup and approval contract as Materials & Pricing Review.
 * Never runs on mount or silently replaces a work item's attached materials. */
export function MaterialLookupControl({ line, sessionKey, onChange }: {
  line: MaterialsLine; sessionKey: string;
  onChange: (value: MaterialAllowanceApproval | null) => void;
}) {
  const lookup = useAutomaticMaterials([line], (_, value) => onChange(value), false, sessionKey);
  const [editing, setEditing] = useState(false);
  const proposal = validMaterialAllowance(line);
  const rows = lineMaterialRows(line, 0);
  const pending = proposal && rows.length > 0 && !materialsApproved(line, true);
  const basis = materialProposalBasis(line);
  const status = lookup.states[basis];
  const busy = !!status && /^(Checking|Identifying)/.test(status);
  if (scopeNeedsNoPurchaseMaterials(line) || (rows.length && !pending)) return null;
  return <div className="mt-2 space-y-2 text-xs" data-testid={`material-lookup-${line.name}`}>
    {pending && <p className="text-amber-700 dark:text-amber-400">Suggested materials — awaiting your approval.</p>}
    {status && <p role="status" className="text-muted-foreground">{status}</p>}
    <div className="flex flex-wrap items-center gap-3">
      <button type="button" className="text-primary underline disabled:opacity-50" disabled={busy}
        onClick={() => lookup.retry(basis)}>Retry material lookup</button>
      {pending && <button type="button" className="text-primary underline" onClick={() => {
        lookup.startManual(basis);
        onChange({ ...proposal, source: "builder", approved: true, manualApproved: true, builderWithheld: false });
      }}>Approve materials</button>}
      {pending && !editing && <button type="button" className="text-primary underline"
        onClick={() => setEditing(true)}>Edit suggested materials</button>}
    </div>
    {(!rows.length || editing) && <MaterialAllowanceControl line={line} onChange={onChange}
      onManualStart={() => { setEditing(true); lookup.startManual(basis); }} />}
  </div>;
}
